/**
 * UserRepository - Gestion des profils utilisateurs
 * Utilise BaseRepository pour la gestion d'erreurs standardisée
 */

import { BaseRepository } from './BaseRepository';
import { RPC } from '@/lib/rpc';
import type { UserProfile, Role, UserStatus } from '@/types';

export interface CreateProfileData {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  phone?: string;
  company_id?: string;
  permissions?: string[];
}

/** Identité minimale d'un profil */
export interface ProfileName {
  id: string;
  first_name: string | null;
  last_name: string | null;
}

/** Profil avec rôle, pour les listes d'utilisateurs */
export interface ProfileSummary extends ProfileName {
  role: string;
}

/** Profil avec rôle et statut */
export interface ProfileWithStatus extends ProfileSummary {
  status: string;
}

/** Profil créé à l'inscription d'un prestataire (insertion sans relecture) */
export interface ProfileInsert {
  id: string;
  role: string;
  first_name: string;
  last_name: string;
  status: string;
  validation_status: string;
  permissions: string[];
}

/** Identité affichée dans l'en-tête de l'application */
export interface ProfileIdentity {
  first_name: string | null;
  last_name: string | null;
  role: string;
}

/** Profil avec e-mail (RPC get_profiles_with_email) */
export interface ProfileWithEmail {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  company_id: string | null;
  role: string | null;
}

/** Prestataire enregistré (RPC get_providers_list) */
export interface RegisteredProvider {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  role: string;
}

/**
 * Profil chargé à l'amorçage de la session. Forme non garantie par la base :
 * l'appelant la valide (isValidProfileData) avant usage.
 */
export interface AuthProfileRow {
  id: string;
  role: Role;
  company_id?: string;
  status: UserStatus;
  permissions: unknown;
  default_expense_type_id?: string | null;
  default_subcategory_id?: string | null;
}

/** Colonnes du profil chargé à l'amorçage de la session */
const AUTH_PROFILE_COLUMNS =
  'id, role, company_id, status, permissions, default_expense_type_id, default_subcategory_id';

/**
 * Repository pour les profils utilisateurs
 */
export class UserRepository extends BaseRepository {
  private static readonly PROFILES_TABLE = 'profiles';

  /**
   * Crée un nouveau profil utilisateur
   */
  static async createProfile(profileData: CreateProfileData): Promise<UserProfile> {
    const data = await this.executeMutation<UserProfile>(
      this.supabase
        .from(this.PROFILES_TABLE)
        .insert(profileData)
        .select()
        .single(),
      'Create user profile'
    );

    return data;
  }

  /**
   * Récupère un profil par ID
   */
  static async getById(userId: string): Promise<UserProfile> {
    const data = await this.executeQuery<UserProfile>(
      this.supabase
        .from(this.PROFILES_TABLE)
        .select('*')
        .eq('id', userId)
        .single(),
      `Fetch profile ${userId}`
    );

    return data;
  }

  /**
   * Indique si un profil existe pour cet utilisateur.
   * Toute erreur (ligne absente, RLS, réseau) vaut « pas de profil ».
   */
  static async exists(userId: string): Promise<boolean> {
    const { data, error } = await this.supabase
      .from(this.PROFILES_TABLE)
      .select('id')
      .eq('id', userId)
      .single();

    return !error && !!data;
  }

  /**
   * Profil chargé à l'amorçage de la session (à valider par l'appelant)
   */
  static async getAuthProfile(userId: string): Promise<AuthProfileRow> {
    return this.executeQuery<AuthProfileRow>(
      this.supabase
        .from(this.PROFILES_TABLE)
        .select(AUTH_PROFILE_COLUMNS)
        .eq('id', userId)
        .single(),
      `Fetch auth profile ${userId}`
    );
  }

  /** Prénom, nom et rôle (en-tête de l'application) */
  static async getIdentity(userId: string): Promise<ProfileIdentity> {
    return this.executeQuery<ProfileIdentity>(
      this.supabase
        .from(this.PROFILES_TABLE)
        .select('first_name, last_name, role')
        .eq('id', userId)
        .single(),
      `Fetch profile identity ${userId}`
    );
  }

  /** Prénom et nom d'un profil */
  static async getName(userId: string): Promise<Omit<ProfileName, 'id'>> {
    return this.executeQuery<Omit<ProfileName, 'id'>>(
      this.supabase
        .from(this.PROFILES_TABLE)
        .select('first_name, last_name')
        .eq('id', userId)
        .single(),
      `Fetch profile name ${userId}`
    );
  }

  /** Tous les profils avec leur e-mail (RPC sécurisée, remplace la vue profiles_with_email) */
  static async getAllWithEmail(): Promise<ProfileWithEmail[]> {
    return this.executeQueryArray<ProfileWithEmail>(
      this.supabase.rpc(RPC.GET_PROFILES_WITH_EMAIL),
      'Fetch profiles with email'
    );
  }

  /** Prestataires enregistrés (RPC get_providers_list) */
  static async getRegisteredProviders(): Promise<RegisteredProvider[]> {
    return this.executeQueryArray<RegisteredProvider>(
      this.supabase.rpc(RPC.GET_PROVIDERS_LIST),
      'Fetch registered providers'
    );
  }

  /**
   * Indique si l'utilisateur possède une identité Google liée.
   * Pas de executeQuery : `false` est une réponse valide, pas une absence de données.
   */
  static async hasGoogleIdentity(userId: string | undefined): Promise<boolean> {
    const { data, error } = await this.supabase.rpc(RPC.CHECK_USER_HAS_GOOGLE_IDENTITY, {
      target_user_id: userId,
    });

    if (error) throw this.handleError(error, 'Check Google identity');
    return data as boolean;
  }

  /**
   * Profils actifs des rôles donnés, triés par prénom (avec statut)
   */
  static async getActiveByRoles(roles: readonly string[]): Promise<ProfileWithStatus[]> {
    return this.executeQueryArray<ProfileWithStatus>(
      this.supabase
        .from(this.PROFILES_TABLE)
        .select('id, first_name, last_name, role, status')
        .in('role', roles)
        .eq('status', 'active')
        .order('first_name', { ascending: true }),
      'Fetch active profiles by roles'
    );
  }

  /**
   * Profils actifs des rôles donnés, triés par prénom (sans statut)
   */
  static async getActiveSummariesByRoles(roles: readonly string[]): Promise<ProfileSummary[]> {
    return this.executeQueryArray<ProfileSummary>(
      this.supabase
        .from(this.PROFILES_TABLE)
        .select('id, first_name, last_name, role')
        .in('role', roles)
        .eq('status', 'active')
        .order('first_name', { ascending: true }),
      'Fetch active profile summaries by roles'
    );
  }

  /**
   * Tous les profils des rôles donnés, quel que soit leur statut (sans tri)
   */
  static async getSummariesByRoles(roles: readonly string[]): Promise<ProfileSummary[]> {
    return this.executeQueryArray<ProfileSummary>(
      this.supabase
        .from(this.PROFILES_TABLE)
        .select('id, first_name, last_name, role')
        .in('role', roles),
      'Fetch profile summaries by roles'
    );
  }

  /**
   * Noms des profils donnés
   */
  static async getNamesByIds(ids: string[]): Promise<ProfileName[]> {
    return this.executeQueryArray<ProfileName>(
      this.supabase
        .from(this.PROFILES_TABLE)
        .select('id, first_name, last_name')
        .in('id', ids),
      'Fetch profile names'
    );
  }

  /**
   * Insère un profil sans relire la ligne (inscription prestataire)
   */
  static async insert(row: ProfileInsert): Promise<void> {
    const { error } = await this.supabase
      .from(this.PROFILES_TABLE)
      .insert(row);

    if (error) throw this.handleError(error, 'Insert profile');
  }

  /**
   * Met à jour des champs d'un profil sans relire la ligne
   */
  static async updateFields(
    userId: string,
    fields: { first_name?: string; last_name?: string }
  ): Promise<void> {
    const { error } = await this.supabase
      .from(this.PROFILES_TABLE)
      .update(fields)
      .eq('id', userId);

    if (error) throw this.handleError(error, `Update profile fields ${userId}`);
  }

  /**
   * Rattache des profils à une entreprise (ou les détache avec null).
   * Sans contexte d'erreur : le message est affiché tel quel à l'utilisateur.
   */
  static async setCompanyForIds(ids: string[], companyId: string | null): Promise<void> {
    const { error } = await this.supabase
      .from(this.PROFILES_TABLE)
      .update({ company_id: companyId })
      .in('id', ids);

    if (error) throw this.handleError(error);
  }

  /**
   * Met à jour un profil
   */
  static async update(
    userId: string,
    updates: Partial<CreateProfileData>
  ): Promise<UserProfile> {
    const data = await this.executeMutation<UserProfile>(
      this.supabase
        .from(this.PROFILES_TABLE)
        .update(updates)
        .eq('id', userId)
        .select()
        .single(),
      `Update profile ${userId}`
    );

    return data;
  }
}
